import { useState, useEffect, useMemo } from 'react';
import { trpc } from '../../../lib/trpc.js';
import { getStoredUser } from '../../../lib/auth.js';

interface KeywordRow {
  keyword: string | null;
  matchType: string | null;
  cost: number;
  clicks: number;
  impressions: number;
  conversions: number;
  revenue: number;
  roas: number;
}

type SortKey = 'keyword' | 'matchType' | 'cost' | 'clicks' | 'impressions' | 'conversions' | 'revenue' | 'roas';

const matchTypeBadge: Record<string, string> = {
  EXACT: 'bg-blue-500/15 text-blue-400 border border-blue-500/30',
  PHRASE: 'bg-violet-500/15 text-violet-400 border border-violet-500/30',
  BROAD: 'bg-slate-700 text-slate-400 border border-slate-600',
  Exact: 'bg-blue-500/15 text-blue-400 border border-blue-500/30',
  Phrase: 'bg-violet-500/15 text-violet-400 border border-violet-500/30',
  Broad: 'bg-slate-700 text-slate-400 border border-slate-600',
};

function SortIcon({ dir }: { dir: 'asc' | 'desc' | null }) {
  if (!dir) return <span className="ml-1 text-slate-600">↕</span>;
  return <span className="ml-1 text-blue-400">{dir === 'asc' ? '↑' : '↓'}</span>;
}

function SkeletonRow() {
  return (
    <tr className="animate-pulse">
      {Array.from({ length: 8 }).map((_, i) => (
        <td key={i} className="px-4 py-3">
          <div className="h-4 bg-slate-800 rounded w-16" />
        </td>
      ))}
    </tr>
  );
}

export function KeywordTable() {
  const [rows, setRows] = useState<KeywordRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('cost');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    const user = getStoredUser();
    if (!user) { setLoading(false); return; }
    const today = new Date().toISOString().slice(0, 10);
    trpc.roas.byKeyword.query({ tenantId: user.tenantId, startDate: today, endDate: today })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((data: any[]) => setRows(data))
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false));
  }, []);

  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortKey(key); setSortDir('desc'); }
  }

  const filtered = useMemo(() => {
    return rows
      .filter(kw => (kw.keyword ?? '').toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => {
        const av = a[sortKey];
        const bv = b[sortKey];
        const cmp = typeof av === 'string'
          ? (av ?? '').localeCompare(bv as string ?? '')
          : ((av as number) ?? 0) - ((bv as number) ?? 0);
        return sortDir === 'asc' ? cmp : -cmp;
      });
  }, [rows, search, sortKey, sortDir]);

  function th(label: string, key: SortKey) {
    return (
      <th
        className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-300 select-none whitespace-nowrap"
        onClick={() => toggleSort(key)}
      >
        {label}<SortIcon dir={sortKey === key ? sortDir : null} />
      </th>
    );
  }

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-sm overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-slate-800">
        <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mr-auto">Keywords</h3>
        <input
          type="text"
          placeholder="Search keywords…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-300 placeholder-slate-600 focus:outline-none focus:border-blue-500 w-48"
        />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-950/50">
            <tr>
              {th('Keyword', 'keyword')}
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Match</th>
              {th('Impr.', 'impressions')}
              {th('Clicks', 'clicks')}
              {th('Cost', 'cost')}
              {th('Conv.', 'conversions')}
              {th('Revenue', 'revenue')}
              {th('ROAS', 'roas')}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {loading ? (
              Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} />)
            ) : error ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-slate-500 text-sm">Failed to load keyword data.</td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-slate-500 text-sm">
                  {rows.length === 0 ? 'No keyword data available yet.' : 'No keywords match your search.'}
                </td>
              </tr>
            ) : (
              filtered.map((kw, i) => (
                <tr key={i} className="hover:bg-slate-800/40 transition-colors">
                  <td className="px-4 py-3 text-sm text-slate-200 font-medium whitespace-nowrap">
                    {kw.keyword ?? '(unknown)'}
                  </td>
                  <td className="px-4 py-3">
                    {kw.matchType && (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${matchTypeBadge[kw.matchType] ?? 'bg-slate-700 text-slate-400'}`}>
                        {kw.matchType}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-400">{kw.impressions.toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm text-slate-300">{kw.clicks.toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm text-rose-400">${kw.cost.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                  <td className="px-4 py-3 text-sm text-slate-300">{kw.conversions.toFixed(1)}</td>
                  <td className="px-4 py-3 text-sm text-emerald-400">
                    {kw.revenue > 0 ? `$${kw.revenue.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {kw.roas > 0 ? (
                      <span className={kw.roas >= 2.5 ? 'text-emerald-400' : kw.roas >= 1.5 ? 'text-amber-400' : 'text-rose-400'}>
                        {kw.roas.toFixed(2)}x
                      </span>
                    ) : <span className="text-slate-600">—</span>}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
